import React, { useEffect, useState } from "react";
import {
  retrieveRawData,
  retrieveSelectors,
  retrieveParsedData,
  saveSelectors,
  retrieveRawDataById,
} from "./dataService";
import RawJsonDataDisplay from "./RawJsonDataDisplay";
import MainSelectorsDisplay from "./MainSelectorsDisplay";
import {
  CarDetails,
  Country,
  emptyJsonSelectors,
  Selectors,
  SelectorWithVersion,
  carDetailsFields as carDetailsFieldsInitial,
} from "./types";
import SelectorsEditor from "./Selectors";
import { Container, Spinner } from "reactstrap";
import "./Plugin.css"; // Importation du fichier CSS pour les styles personnalisés
import { Allotment } from "allotment";
import "allotment/dist/style.css"; // Styles Allotment
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRedo,
  faPlay,
  faSave,
  faSyncAlt,
  faSearch,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";

interface PluginProps {
  website: string;
  country: Country;
  countries: Country[];
  vertical: string;
  user: string;
}

const Plugin: React.FC<PluginProps> = ({
  website,
  country,
  vertical,
  countries,
  user,
}) => {
  // État pour le pays sélectionné pour les données brutes uniquement
  const [selectedRawDataCountry, setSelectedRawDataCountry] = useState<Country>(
    country === "default" ? countries[0] : country,
  );
  const [, setFormat] = useState<"JSON">("JSON");
  const [rawDataIndex, setRawDataIndex] = useState(0);
  const [parsedData, setParsedData] = useState<any>({});
  const [parsedDataErrors, setParsedDataErrors] = useState<any>({});

  const [rawDataJsons, setRawDataJsons] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [itemId, setItemId] = useState<string>("");
  const [loadingById, setLoadingById] = useState(false);

  const [selectors, setSelectors] = useState<
    SelectorWithVersion[]
  >([
    {
      country: "default",
      selectors: emptyJsonSelectors,
      version: "", // Version par défaut vide pour l'état initial
    },
  ]);

  const handlerSelectorsChange = (updatedCountrySelectors: Selectors) => {
    const updatedSelectors = selectors.map((s) => {
      if (s.country === country) {
        return { ...s, selectors: updatedCountrySelectors };
      }
      return s;
    });
    setSelectors(updatedSelectors);
  };

  const addMainSelector = ({ name }: { name: string }) => {
    setSelectors(
      selectors.map((s) => {
        if (s.country === country) {
          return {
            ...s,
            selectors: {
              ...s.selectors,
              [name]: { type: "MainSelector", value: "" },
            },
          };
        }
        return s;
      }),
    );
  };

  const removeMainSelector = (name: string) => {
    setSelectors(
      selectors.map((s) => {
        if (s.country === country) {
          const newSelectors = { ...s.selectors };
          delete newSelectors[name as keyof typeof newSelectors];
          return { ...s, selectors: newSelectors as Selectors };
        }
        return s;
      }),
    );
  };

  async function rawData(force = false) {
    setLoadingRaw(true);
    try {
      const { data, type } = await retrieveRawData({
        country: selectedRawDataCountry,
        website,
        vertical,
        force,
      });

      if (type === "JSON") {
        setRawDataJsons(data || []);
        setFormat("JSON");
      }
    } catch (error) {
      window.alert(
        "Une erreur est survenue lors de la récupération des données brutes. Veuillez réessayer.",
      );
    }
    setLoadingRaw(false);
  }

  async function rawDataById() {
    if (!itemId) {
      window.alert("Veuillez saisir un ID valide");
      return;
    }

    setLoadingById(true);
    try {
      const data = await retrieveRawDataById({
        vertical,
        country: selectedRawDataCountry,
        website,
        id: itemId,
      });

      setRawDataJsons([data]);
      setRawDataIndex(0);
      setFormat("JSON");
    } catch (error) {
      window.alert(
        "Une erreur est survenue lors de la récupération des données par ID. Veuillez vérifier l'ID et réessayer.",
      );
    }
    setLoadingById(false);
  }

  async function getSelectors(force = false) {
    setLoading(true);
    try {
      const data = await retrieveSelectors({ website, vertical, force });
      if (data.length > 0) {
        setSelectors(data);
      }
    } catch (error) {
      window.alert(
        "Une erreur est survenue lors de la récupération des sélecteurs. Veuillez réessayer.",
      );
    }
    setLoading(false);
  }

  async function handleTestParser() {
    setLoading(true);
    try {
      const { results: parsedData, errors } = await retrieveParsedData({
        rawData: rawDataJsons[rawDataIndex],
        selectors: selectors.find((s) => s.country === country)!.selectors,
        type: "JSON",
      });
      setParsedDataErrors({
        [rawDataIndex]: errors,
      });
      setParsedData({
        [rawDataIndex]: parsedData,
      });
    } catch (e) {}
    setLoading(false);
  }

  async function handleSaveSelectors() {
    const isConfirmed = window.confirm(
      "Êtes-vous sûr de vouloir enregistrer ces sélecteurs ? Ils seront utilisés pour les prochains crawls",
    );

    if (!isConfirmed) {
      return; // Si l'utilisateur annule, on stoppe l'exécution
    }
    setLoading(true);
    try {
      await saveSelectors({
        website,
        vertical,
        selectors,
        user,
      });
    } catch (error) {
      window.alert(
        "Une erreur est survenue lors de l'enregistrement des sélecteurs. Veuillez réessayer.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    rawData();
    getSelectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRawDataCountry, website, vertical]);

  const selectorsForCountry: Selectors = selectors.find(
    (s) => s.country === country || s.country === "default",
  )!.selectors;

  // Récupération de la version du parser pour le pays actuel
  const currentParserVersion = selectors.find(
    (s) => s.country === country || s.country === "default",
  )?.version || "";

  const mainSelectors: typeof carDetailsFieldsInitial = Object.entries(
    selectorsForCountry,
  )
    .filter(([name, s]) => s.type === ("MainSelector" as any))
    .map(([name, s]) => ({
      name: name as keyof CarDetails,
      type: "string",
      comment: "",
      context: "mainSelector",
    }));

  const fields: typeof carDetailsFieldsInitial = [
    ...mainSelectors,
    ...carDetailsFieldsInitial,
  ];

  return (
    <Container fluid className="plugin-container">
      {/* En-tête de l'application */}
      <div className="app-header">
        <h1 className="app-title">
          {website} - {vertical} - {user}
        </h1>
        {currentParserVersion && (
          <div className="parser-version">
            <small>Version Parser: {currentParserVersion}</small>
          </div>
        )}
        {(loading || loadingRaw || loadingById) && (
          <div className="loading-indicator">
            <Spinner size="sm" />
            {loadingRaw && <span>Chargement des données brutes...</span>}
            {loadingById && <span>Chargement des données par ID...</span>}
            {loading && !loadingRaw && !loadingById && (
              <span>Traitement en cours...</span>
            )}
          </div>
        )}
      </div>

      {/* Conteneur des contrôles */}
      <div className="controls-container">
        {/* Layout des commandes en deux colonnes */}
        <div className="commands-layout">
          {/* Colonne de gauche: Commandes de données */}
          <div className="commands-column commands-column-left">
            <div className="data-commands-section">
              {/* Sélecteur de pays pour les données brutes */}
              {countries.length > 1 && (
                <div className="country-selector-row">
                  <span className="country-label">Pays (données brutes):</span>
                  <select
                    className="country-selector"
                    value={selectedRawDataCountry}
                    onChange={(e) =>
                      setSelectedRawDataCountry(e.target.value as Country)
                    }
                  >
                    {countries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn-refresh-country"
                    onClick={() => rawData(true)}
                    title="Rafraîchir les données pour ce pays"
                  >
                    <FontAwesomeIcon icon={faGlobe} className="btn-icon" />
                  </button>
                </div>
              )}

              <div className="example-row">
                <span className="example-label">Exemple:</span>
                <select
                  className="example-selector"
                  onChange={(e) => setRawDataIndex(parseInt(e.target.value))}
                  defaultValue={0}
                >
                  {Array.from({
                    length: rawDataJsons.length,
                  }).map((_, i) => (
                    <option key={i} value={i}>
                      Exemple {i + 1}
                    </option>
                  ))}
                </select>
                <button className="btn-new-data" onClick={() => rawData(true)}>
                  <FontAwesomeIcon icon={faSyncAlt} className="btn-icon" />
                  Nouveau jeu
                </button>
              </div>

              <div className="id-row">
                <span className="id-label">ID</span>
                <input
                  type="text"
                  className="id-input"
                  placeholder="Saisir un ID"
                  value={itemId}
                  onChange={(e) => setItemId(e.target.value)}
                />
                <button
                  className="btn-get-id"
                  onClick={rawDataById}
                  disabled={!itemId || loadingById}
                >
                  <FontAwesomeIcon icon={faSearch} className="btn-icon" />
                  Récupérer par ID
                </button>
              </div>
            </div>
          </div>

          {/* Colonne de droite: Commandes du parser */}
          <div className="commands-column commands-column-right">
            <div className="parser-buttons">
              <button className="btn-reset" onClick={() => getSelectors(true)}>
                <FontAwesomeIcon icon={faRedo} className="btn-icon" />
                Réinitialiser
              </button>

              <button className="btn-test" onClick={handleTestParser}>
                <FontAwesomeIcon icon={faPlay} className="btn-icon" />
                Tester Parse
              </button>

              <button className="btn-save" onClick={handleSaveSelectors}>
                <FontAwesomeIcon icon={faSave} className="btn-icon" />
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="content-container">
        <Allotment>
          <Allotment.Pane minSize={200} preferredSize="30%">
            <div className="raw-data-col">
              <MainSelectorsDisplay
                mainSelectors={mainSelectors.map(selector => selector.name as string)}
                parsedData={parsedData[rawDataIndex]}
              />
              <div className="raw-data-separator">
                <RawJsonDataDisplay jsons={rawDataJsons} index={rawDataIndex} />
              </div>
            </div>
          </Allotment.Pane>
          <Allotment.Pane>
            <div className="selector-col">
              <SelectorsEditor
                carDetailsFields={fields}
                selectors={selectorsForCountry}
                setSelectors={handlerSelectorsChange}
                removeMainSelector={removeMainSelector}
                addMainSelector={addMainSelector}
                parsedData={parsedData[rawDataIndex]}
                type="JSON"
                errors={parsedDataErrors[rawDataIndex]}
                rawData={rawDataJsons[rawDataIndex]}
              />
            </div>
          </Allotment.Pane>
        </Allotment>
      </div>
    </Container>
  );
};

export default Plugin;

// Composants auxiliaires (FormatSelector et RawDataIndexSelector)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FormatSelector: React.FC<{
  setFormat: (format: "JSON" | "HTML") => void;
}> = ({ setFormat }) => {
  return (
    <div className="mb-3">
      <label htmlFor="format-selector" className="mr-2">
        <strong>Format:</strong>
      </label>
      <select
        id="format-selector"
        onChange={(e) => setFormat(e.target.value as "JSON" | "HTML")}
        defaultValue={"JSON"}
      >
        <option value="JSON">JSON</option>
        <option value="HTML">HTML</option>
      </select>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RawDataIndexSelector: React.FC<{
  count: number;
  setIndex: (index: number) => void;
}> = ({ count, setIndex }) => {
  return (
    <div className="mb-3">
      <label htmlFor="data-index-selector" className="mr-2">
        <strong>Choisir une brute en exemple:</strong>
      </label>
      <select
        id="data-index-selector"
        onChange={(e) => setIndex(parseInt(e.target.value))}
        defaultValue={0}
      >
        {Array.from({ length: count }).map((_, i) => (
          <option key={i} value={i}>
            Exemple {i + 1}
          </option>
        ))}
      </select>
    </div>
  );
};
